import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Settings, Shield, Trash2, Plus, AlertTriangle, Save, RefreshCw, Ban, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubmissionAdminDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Thresholds state
  const [thresholds, setThresholds] = useState({
    min_net_votes: 3,
    min_total_votes: 5,
    max_age_days: 30,
    banned_users_can_submit: false,
    warned_users_can_submit: true,
    min_reputation_to_submit: -50,
    daily_submission_limit: 5,
    low_rep_daily_limit: 1,
    low_rep_threshold: 0,
    viral_min_net_votes: 10,
    viral_min_total_votes: 15
  });
  
  // Blocked sources state
  const [blockedSources, setBlockedSources] = useState([]);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [newBlock, setNewBlock] = useState({ url: '', domain: '', reason: '', block_type: 'permanent' });
  
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);
  
  const fetchData = async () => {
    setLoading(true);
    
    try {
      // Fetch thresholds
      const thresholdsRes = await fetch(`${API_URL}/api/submissions/thresholds`, { credentials: 'include' });
      if (thresholdsRes.ok) {
        const data = await thresholdsRes.json();
        setThresholds(data);
      }
      
      // Fetch blocked sources
      const blockedRes = await fetch(`${API_URL}/api/submissions/blocked-sources`, { credentials: 'include' });
      if (blockedRes.ok) {
        const data = await blockedRes.json();
        setBlockedSources(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/submissions/thresholds`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(thresholds)
      });
      
      if (res.ok) {
        toast.success('Thresholds saved successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Error saving thresholds');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddBlock = async () => {
    if (!newBlock.url && !newBlock.domain) {
      toast.error('Please provide a URL or domain to block');
      return;
    }
    if (!newBlock.reason) {
      toast.error('Please provide a reason for blocking');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/submissions/blocked-sources`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBlock)
      });
      
      if (res.ok) {
        const data = await res.json();
        setBlockedSources([data, ...blockedSources]);
        setBlockDialogOpen(false);
        setNewBlock({ url: '', domain: '', reason: '', block_type: 'permanent' });
        toast.success('Source blocked');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to block');
      }
    } catch (err) {
      toast.error('Error blocking source');
    }
  };
  
  const handleUnblock = async (blockId) => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/blocked-sources/${blockId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        setBlockedSources(blockedSources.filter(b => b.id !== blockId));
        toast.success('Source unblocked');
      } else {
        toast.error('Failed to unblock');
      }
    } catch (err) {
      toast.error('Error unblocking source');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="h-8 w-8 text-gray-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Submission Settings</h1>
            <p className="text-gray-600">Configure thresholds and manage blocked sources</p>
          </div>
        </div>
        
        <Tabs defaultValue="thresholds">
          <TabsList className="mb-4">
            <TabsTrigger value="thresholds" data-testid="tab-thresholds">
              <Settings className="h-4 w-4 mr-2" /> Thresholds
            </TabsTrigger>
            <TabsTrigger value="blocked" data-testid="tab-blocked">
              <Ban className="h-4 w-4 mr-2" /> Blocked Sources
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="thresholds">
            <Card>
              <CardHeader>
                <CardTitle>Promotion Thresholds</CardTitle>
                <CardDescription>
                  Configure when submissions get promoted to the curated feed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Promotion Thresholds */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Basic Promotion</h4>
                    <div>
                      <Label>Minimum Net Votes</Label>
                      <Input
                        type="number"
                        value={thresholds.min_net_votes}
                        onChange={(e) => setThresholds({...thresholds, min_net_votes: parseInt(e.target.value) || 0})}
                        min={1}
                        max={100}
                        data-testid="input-min-net-votes"
                      />
                      <p className="text-xs text-gray-500 mt-1">Upvotes minus downvotes needed</p>
                    </div>
                    <div>
                      <Label>Minimum Total Votes</Label>
                      <Input
                        type="number"
                        value={thresholds.min_total_votes}
                        onChange={(e) => setThresholds({...thresholds, min_total_votes: parseInt(e.target.value) || 0})}
                        min={1}
                        max={100}
                        data-testid="input-min-total-votes"
                      />
                      <p className="text-xs text-gray-500 mt-1">Total engagement required</p>
                    </div>
                    <div>
                      <Label>Max Age (days)</Label>
                      <Input
                        type="number"
                        value={thresholds.max_age_days}
                        onChange={(e) => setThresholds({...thresholds, max_age_days: parseInt(e.target.value) || 0})}
                        min={1}
                        max={365}
                        data-testid="input-max-age"
                      />
                      <p className="text-xs text-gray-500 mt-1">Submissions expire after this</p>
                    </div>
                  </div>
                  
                  {/* Viral Thresholds */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Viral (Cross-Club)</h4>
                    <div>
                      <Label>Viral Net Votes</Label>
                      <Input
                        type="number"
                        value={thresholds.viral_min_net_votes}
                        onChange={(e) => setThresholds({...thresholds, viral_min_net_votes: parseInt(e.target.value) || 0})}
                        min={1}
                        max={500}
                        data-testid="input-viral-net-votes"
                      />
                      <p className="text-xs text-gray-500 mt-1">For cross-club visibility</p>
                    </div>
                    <div>
                      <Label>Viral Total Votes</Label>
                      <Input
                        type="number"
                        value={thresholds.viral_min_total_votes}
                        onChange={(e) => setThresholds({...thresholds, viral_min_total_votes: parseInt(e.target.value) || 0})}
                        min={1}
                        max={500}
                        data-testid="input-viral-total-votes"
                      />
                    </div>
                  </div>
                </div>
                
                <hr />
                
                {/* Reputation Settings */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Reputation & Posting Limits</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Minimum Reputation to Submit</Label>
                        <Input
                          type="number"
                          value={thresholds.min_reputation_to_submit}
                          onChange={(e) => setThresholds({...thresholds, min_reputation_to_submit: parseFloat(e.target.value) || 0})}
                          min={-1000}
                          max={0}
                          data-testid="input-min-rep"
                        />
                        <p className="text-xs text-gray-500 mt-1">Users below this can't submit</p>
                      </div>
                      <div>
                        <Label>Low Reputation Threshold</Label>
                        <Input
                          type="number"
                          value={thresholds.low_rep_threshold}
                          onChange={(e) => setThresholds({...thresholds, low_rep_threshold: parseFloat(e.target.value) || 0})}
                          min={-100}
                          max={100}
                          data-testid="input-low-rep-threshold"
                        />
                        <p className="text-xs text-gray-500 mt-1">Below this = restricted posting</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Daily Submission Limit</Label>
                        <Input
                          type="number"
                          value={thresholds.daily_submission_limit}
                          onChange={(e) => setThresholds({...thresholds, daily_submission_limit: parseInt(e.target.value) || 0})}
                          min={1}
                          max={50}
                          data-testid="input-daily-limit"
                        />
                        <p className="text-xs text-gray-500 mt-1">For normal users</p>
                      </div>
                      <div>
                        <Label>Low-Rep Daily Limit</Label>
                        <Input
                          type="number"
                          value={thresholds.low_rep_daily_limit}
                          onChange={(e) => setThresholds({...thresholds, low_rep_daily_limit: parseInt(e.target.value) || 0})}
                          min={0}
                          max={10}
                          data-testid="input-low-rep-daily-limit"
                        />
                        <p className="text-xs text-gray-500 mt-1">For low-rep users (0 = blocked)</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <hr />
                
                {/* Permission Toggles */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Submission Permissions</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Banned Users Can Submit</Label>
                        <p className="text-xs text-gray-500">Allow club-banned users to submit URLs</p>
                      </div>
                      <Switch
                        checked={thresholds.banned_users_can_submit}
                        onCheckedChange={(val) => setThresholds({...thresholds, banned_users_can_submit: val})}
                        data-testid="switch-banned-submit"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Warned Users Can Submit</Label>
                        <p className="text-xs text-gray-500">Allow users with active warnings to submit</p>
                      </div>
                      <Switch
                        checked={thresholds.warned_users_can_submit}
                        onCheckedChange={(val) => setThresholds({...thresholds, warned_users_can_submit: val})}
                        data-testid="switch-warned-submit"
                      />
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveThresholds} disabled={saving} className="w-full" data-testid="save-thresholds">
                  {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Thresholds
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="blocked">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Blocked Sources</CardTitle>
                  <CardDescription>URLs and domains that cannot be submitted</CardDescription>
                </div>
                <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-block-btn">
                      <Plus className="h-4 w-4 mr-2" /> Block Source
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Block a Source</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>URL (optional)</Label>
                        <Input
                          placeholder="https://specific-page.com/spam"
                          value={newBlock.url}
                          onChange={(e) => setNewBlock({...newBlock, url: e.target.value})}
                          data-testid="block-url-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Block a specific URL</p>
                      </div>
                      <div>
                        <Label>Domain (optional)</Label>
                        <Input
                          placeholder="spamsite.com"
                          value={newBlock.domain}
                          onChange={(e) => setNewBlock({...newBlock, domain: e.target.value})}
                          data-testid="block-domain-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Block entire domain</p>
                      </div>
                      <div>
                        <Label>Reason *</Label>
                        <Textarea
                          placeholder="Why is this being blocked?"
                          value={newBlock.reason}
                          onChange={(e) => setNewBlock({...newBlock, reason: e.target.value})}
                          required
                          data-testid="block-reason-input"
                        />
                      </div>
                      <Button onClick={handleAddBlock} className="w-full" data-testid="confirm-block-btn">
                        <Ban className="h-4 w-4 mr-2" /> Block Source
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {blockedSources.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No blocked sources</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>URL/Domain</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Blocked By</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blockedSources.map((block) => (
                        <TableRow key={block.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {block.domain ? (
                                <>
                                  <Globe className="h-4 w-4 text-gray-500" />
                                  <span className="font-mono text-sm">{block.domain}</span>
                                  <Badge variant="secondary">domain</Badge>
                                </>
                              ) : (
                                <span className="font-mono text-sm truncate max-w-[200px]">{block.url}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{block.reason}</TableCell>
                          <TableCell>{block.blocked_by_username}</TableCell>
                          <TableCell>{new Date(block.blocked_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUnblock(block.id)}
                              data-testid={`unblock-${block.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
